import { useCallback, useRef, useState } from 'react'

import { getGoalById } from '@/features/studies/creation/components/goals/goalsConfig'
import {
  studyBriefHandoffState,
  type StudyBriefHandoffState,
} from '@/features/studies/creation/studyBriefHandoff'
import {
  isStudyTypeClosed,
  useClosedStudyTypes,
} from '@/hooks/useClosedStudyTypes'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useCreateStudyEnabled } from '@/hooks/useCreateStudyEnabled'
import { upsertSidebarThread } from '@/components/layout/useSidebarThreads'
import { knowledgeThreadsApi } from '@/lib/api'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { stashHomeStimuli } from '@/lib/home-lab/pendingStimuli'
import i18n from '@/i18n'
import { useRouter } from '@/routing/navigation'
import { ROUTES } from '@/routes/routes'

import { FocusGroupLaunchOptions, FocusGroupLaunchResult, launchFocusGroup } from './focusGroupLaunch'
import type { AssistantEffort } from '@digital-twins/shared-types'
import type { KnowledgeLibraryStatus } from '@/lib/home-lab/library'
import type { HomeLabSegment } from '@/lib/home-lab/segment'
import type { ChatImage, KnowledgeThreadListItem, KnowledgeThreadScope } from '@/lib/api'
import type { HomeLabMode } from './HomeLabModeToggle'

/**
 * Assistant MVP Step 6 — the study hand-off (docs/plans/assistant-mvp-plan.md).
 *
 * `HomeLabStage`'s submit was a deliberate Phase A stub: no resolution, no navigation,
 * a comment saying the wiring was B4. This hook is that wiring, pulled out on its own so
 * the hand-off logic is testable without mounting the composer or a router.
 *
 * THREE mechanisms now, where there were two. `AssistantHandoff`'s own doc comment in
 * `types.ts` still describes a pair (chat and data sharing one hand-off), and that is no
 * longer true of chat:
 *
 *  - STUDY mode navigates to `/tests/new?audience=&goal=&language=` and carries the typed
 *    brief as router state (`studyBriefHandoffState`, C10#14), never as a query param, so
 *    it stays out of browser history and the pageview tracker. `StudyCreationFlow` reads it
 *    with `useStudyBriefHandoff`, skips the goal screen and seeds `initialMessage`. The live
 *    homepage's `handleStudySubmit` (`HomeStage.tsx`) goes through the same
 *    `buildStudyCreationHandoff`, so there is one writer. `?goal=` is read by
 *    `StudyCreationFlow.tsx` and dispatched as `SET_GOAL`, because Home Lab has a
 *    study-type picker the live homepage never had and its value was being dropped here.
 *    See `buildStudyCreationHandoff` below for the full chain that a missing `goalId` broke.
 *  - CHAT mode HANDS NOTHING OFF ANY MORE (docs/plans/home-lab-direct-focus-group.md,
 *    Step 4). It awaits `launchFocusGroup`, which draws the twins and creates the ordinary
 *    group room, and then navigates straight to `ROUTES.chatRoom(token)` carrying the typed
 *    sentence as router-state `initialMessage`. `ChatPage` reads exactly that key off
 *    location state and auto-sends it (`ChatPage.tsx:92-137`), which is the whole reason the
 *    sentence travels this way rather than as a room setting. The page that used to sit in
 *    between bought nothing: the room was already created by the time it painted. Failures
 *    come back as a reason and do NOT navigate, so the composer keeps the user's sentence.
 *  - DATA mode HANDS NOTHING OFF EITHER, AS OF STEP 7 (docs/plans/home-lab-direct-focus-group.md).
 *    It mints a real knowledge thread with `knowledgeThreadsApi.create({ scope })` and
 *    navigates straight to `ROUTES.thread(token)`, carrying the typed sentence as
 *    router-state `pendingMessage`. This mirrors `ThreadsPage.tsx`'s own `handleSend`
 *    (`ThreadsPage.tsx:170-182`) rather than inventing a second way to start a thread: same
 *    typed scope shape, same state key. `ThreadDetailPage` reads exactly that key off
 *    location state and runs the ask (`ThreadDetailPage.tsx:472`), which is the proven
 *    consumer and the reason the sentence travels this way rather than through the create
 *    payload's own `firstMessage` field (`client.ts:1547`) — that field exists but no
 *    frontend code sends it. `buildAssistantHandoff` and the `AssistantHandoff` router-state
 *    plumbing used to stay in this file, exported and unused, because chat and data were the
 *    two consumers that made the chassis reachable and both were rewired away from it before
 *    the chassis itself was deleted. `docs/plans/assistant-platform-v1-execution-plan.md`
 *    Step 0.1 removed the chassis (`Assistant/`, `AnswerBlock/`, `jobs/`) and, with it, the
 *    dead `buildAssistantHandoff` here: it had no caller left outside its own test.
 *
 * ============================================================================
 * COMPOSER LANGUAGE (docs/plans/home-lab-composer-language-and-recent-study.md, Step 6)
 * ============================================================================
 *
 * `HomeLabSubmitInput` gains a REQUIRED `language: string | null`. `HomeLabStage` owns
 * computing the EFFECTIVE value (D2: `override ?? segment?.language ?? null` in chat and
 * study, `override ?? null` in data, D2b) and hands it straight through; this hook never
 * resolves it and never substitutes `'en'` for a null. A null is a real "nothing resolved",
 * not a placeholder, and every branch below sends it as an omission rather than inventing a
 * value, so every existing backend default stays in force:
 *
 *  - STUDY appends `&language=` to the `/tests/new` href, only when non-null
 *    (`buildStudyCreationHandoff` below).
 *  - CHAT passes it straight into `launchFocusGroup`, which puts it on `settings.language`
 *    only when non-null — see that module's own comment for why a picked language is never
 *    an invented one.
 *  - DATA sends it on `knowledgeThreadsApi.create({ scope, language })`, again only when
 *    non-null, binding to the backend contract landed in the same wave (`POST
 *    /knowledge/threads` accepts an optional `language`, validated server-side).
 */

export interface HomeLabSubmitInput {
  mode: HomeLabMode
  /** Null until the user picks. Mirrors `HomeLabStage`'s own `segment` state. */
  segment: HomeLabSegment | null
  message: string
  /** Only meaningful in `study` mode. Null until `StudyTypeControls` sets one. */
  studyTypeId: string | null
  /**
   * Only meaningful in `data` mode, where it is the ONLY required slot. Required rather
   * than optional on purpose: this interface is what the `submit()` guard holds, so an
   * optional field would let a caller reach the data branch having stated nothing about
   * the library and be waved through by a default. `'loading'` is the honest value for
   * "not known yet", and it blocks, which is the safe direction.
   */
  libraryStatus: KnowledgeLibraryStatus
  /**
   * Composer Language plan (D2, D2b). The EFFECTIVE language, already resolved by
   * `HomeLabStage`: `override ?? segment?.language ?? null` in chat and study, `override ??
   * null` in data. Null is a real value meaning "nothing resolved", never a placeholder for
   * `'en'`, and every downstream call below omits the field entirely rather than
   * substituting one in. Required, not optional: a caller that forgot to compute it should
   * fail to typecheck rather than silently submit English.
   */
  language: string | null
  /**
   * Assistant Platform v1 Step 6.2 (docs/plans/assistant-platform-v1-execution-plan.md):
   * the composer's effort toggle. UNLIKE `language`, this is OPTIONAL rather than a fourth
   * required field: chat seats a twin focus group and study runs the deterministic
   * questionnaire pipeline, and neither has a `reasoningEffort` knob to feed, so forcing
   * every call site to state a value it has no use for would be the wrong rigor here. Only
   * the `data` branch reads it, and only to carry it forward as `pendingEffort` router
   * state (mirroring `pendingMessage`) for the ask that Step 6.3/7.1 fires once the thread
   * page mounts. Omitted (not defaulted here) when absent, the same omission style
   * `language` already uses for `null`.
   */
  effort?: AssistantEffort
  /**
   * Step A2 (plan 2026-10-21): the Ask composer's Assistant profile id. Optional like
   * `effort`, read only by the `data` branch, and carried as `pendingProfile` router state
   * so the thread's first turn runs under the profile picked here. Omitted when absent.
   */
  profile?: string
  /**
   * Ask-mode source scope from the Home Lab picker. Omitted falls back to Any
   * (web + library, no item filter).
   */
  scope?: KnowledgeThreadScope
  /**
   * Lobby example-prompt publicId (R3.6 / B1). Stamped on thread create when
   * the user started from a chip. Omitted for free-typed asks.
   */
  sourcePromptId?: string | null
  /**
   * Composer model levels plan, Step 5: the chat composer's room choices (speakers first,
   * conversation level, advanced pick). Only the `chat` branch reads it, passing it straight
   * to `launchFocusGroup`, which sends only the non-default values. Optional like `effort`:
   * study and data have nothing to do with it.
   */
  focusGroupOptions?: FocusGroupLaunchOptions
  /**
   * The home `+` tiles, already hosted. Read by the `chat` and `data` branches only:
   * stashed under the new room or thread token (`pendingStimuli.ts`) right before the
   * navigation, and the destination sends its first message with them.
   */
  stimuli?: Array<ChatImage>
}

/**
 * REQUIRED CHOICES (mirrors `HomeLabStage`'s header comment, word for word): neither the
 * audience/segment nor, in study mode, the study type has a default. A default here would
 * be silently answered on the user's behalf and then run, which is the one thing a study
 * cannot afford. Exported so `HomeComposer`'s `submitBlocked` prop and this hook's own
 * guard read the exact same rule rather than two copies of it drifting apart.
 *
 * ============================================================================
 * DATA MODE IS EXEMPT FROM THE SEGMENT REQUIREMENT, DELIBERATELY
 * ============================================================================
 *
 * Written down because it reads like an oversight and is not, and because the obvious
 * "fix" is to put the segment back: data mode asks the ORG'S OWN DOCUMENTS, and no
 * audience takes part in that. `knowledgeApi.askStream`'s scope is
 * `{ documentTokens, collectionTokens }`, with no audience field anywhere in it, so a
 * chosen segment could not reach the ask even if one were demanded. Demanding one anyway
 * would make the user pick 500 twins in order to question a PDF, and would imply an
 * influence over the answer that the segment does not have. This is the same reason
 * `HomeLabStage` hides `SegmentChip` in this mode rather than showing an optional one.
 *
 * What data mode requires instead is a library that can actually be searched: `'ready'`
 * and nothing else. `'loading'`, `'preparing'` and `'error'` all block, each for its own
 * honest reason, and `HomeLabStage` says which one on the page rather than leaving a dead
 * submit button. The precondition is checked HERE, before the ask, because an empty
 * library is a precondition that is not met rather than a question that found no answer,
 * and spending a round trip to be told so is a worse experience than being told up front.
 *
 * The branch is on MODE so the chat and study rules below are untouched: both still need
 * the segment, and study still needs its type, exactly as before.
 *
 * ============================================================================
 * THE LIBRARY PRECONDITION ITSELF LIFTS BEHIND THE ASSISTANT PLATFORM FLAG (D4)
 * ============================================================================
 *
 * Assistant Platform v1 Step 6.2: once `assistantPlatformEnabled` is true, an empty (or
 * loading, preparing, or errored) library stops being an unmet precondition for data mode,
 * because the assistant can search the web and reason with no indexed documents at all —
 * the library becomes one optional source among several rather than the only one. The
 * function still takes the same input shape; a second, OPTIONAL `options` argument carries
 * the flag so every existing caller that has not been taught about it yet (there were none
 * at the time this landed) keeps compiling and keeps the pre-flag behaviour by default.
 * `HomeLabStage` demotes its "your library is empty" card under the same flag, so the
 * rendered gate and this function's answer never disagree with each other.
 */
export function isHomeLabSubmitReady(
  input: Pick<HomeLabSubmitInput, 'mode' | 'segment' | 'studyTypeId' | 'libraryStatus'>,
  options?: { assistantPlatformEnabled?: boolean },
): boolean {
  if (input.mode === 'data') {
    if (options?.assistantPlatformEnabled) {
      return true
    }
    return input.libraryStatus === 'ready'
  }
  if (input.mode === 'study') {
    return input.studyTypeId !== null
  }
  return input.segment !== null
}

/**
 * The study hand-off: `/tests/new?audience=&goal=&language=` plus the brief as router state.
 *
 * THE ONE WRITER (C10#14). Both Home surfaces (this hook and `HomeStage.tsx`'s
 * `handleStudySubmit`) call it, and the brief goes into `studyBriefHandoffState`, never
 * into the URL: a `?message=` lands in browser history and in the pageview tracker's
 * `pathname?search`. `StudyCreationFlow`'s `useStudyBriefHandoff` is the one reader.
 *
 * `goal` carries Home Lab's study-type pick. Dropping it meant the pick died on the
 * homepage: `StudyCreationFlow`'s `state.goalId` stayed `null`, which (a) skipped the goal
 * screen anyway because a brief was present, so the user could not re-pick, (b) sent no
 * `goalId` on session creation (`useQuestionnaireDraftSession.ts` spreads it only when
 * truthy), so the backend had no `OPENING_CHAINS[goalId]` and asked its generic objective
 * question, and (c) left `is_known_arc(None)` false in `quick_study/agent.py`, so the arc
 * fell back to heuristics.
 *
 * `getGoalById` is the VALIDATOR here, not merely a lookup, and it is the canonical one
 * (`goalsConfig.ts`) rather than a second list that could drift from it. It returns null for
 * `paste_questionnaire`, which is a Home Lab action and not a goal, and for any id that is
 * not one of the eight — so a junk value emits no param instead of a param the flow would
 * have to defend against. `explore` DOES pass, deliberately: "Ask anything" is the `explore`
 * goal (the `generic` arc), per `studyTypeLookup.ts`'s own note. The id written is
 * `goal.id`, not the caller's string, so the URL always carries the canonical spelling.
 *
 * `language` is appended ONLY when non-null: a null effective language means nothing was
 * resolved (D2), so the param is omitted rather than sent as an empty string.
 * `StudyCreationFlow.tsx` reads `?language=` and dispatches `SET_CONTENT_LANGUAGE`.
 */
export function buildStudyCreationHandoff(params: {
  audienceId: string | null
  message: string
  studyTypeId: string | null
  language: string | null
}): { href: string; state: StudyBriefHandoffState | undefined } {
  const searchParams = new URLSearchParams()
  if (params.audienceId) {
    searchParams.set('audience', params.audienceId)
  }
  const goal = getGoalById(params.studyTypeId)
  if (goal) {
    searchParams.set('goal', goal.id)
  }
  if (params.language) {
    searchParams.set('language', params.language)
  }
  return {
    href: `${ROUTES.testsNew}?${searchParams.toString()}`,
    state: studyBriefHandoffState(params.message),
  }
}

/** `pending` while a focus group is being seated, and one status per way it can end. */
export type HomeLabSubmitStatus = 'idle' | 'pending' | 'failed' | 'trial_capped'

export interface HomeLabSubmitState {
  status: HomeLabSubmitStatus
  /** User-facing reason when status is 'failed'. Null otherwise. */
  message: string | null
}

/**
 * `trial_capped` is its OWN status rather than a `failed` with a particular message, because
 * the stage does a different thing with it: it opens `TrialLimitUpgradeModal` instead of
 * printing a reason under the composer. Matching on message text would be the alternative,
 * and it would break the first time the copy was edited.
 */
export interface UseHomeLabSubmitResult {
  submit: (input: HomeLabSubmitInput) => Promise<void>
  state: HomeLabSubmitState
  /** Clears a failure so the composer can drop the inline reason on the next edit. */
  reset: () => void
}

const IDLE_STATE: HomeLabSubmitState = { status: 'idle', message: null }

/**
 * The launcher and the thread creator, both injectable, both optional.
 *
 * `launchFocusGroup` reaches two API edges of its own, and it already takes its own deps for
 * that. Threading a fake all the way down from a hook test would mean stating the persona
 * draw and the room create just to assert where the router went, so the hook takes the whole
 * launch as one seam instead. `createThread` follows the same shape for the data branch: a
 * test injects a fake rather than mocking `knowledgeThreadsApi` at the network. Both are
 * optional so a caller that only cares about one branch (as every test below does) states
 * only that one; the real function is the default for whichever is omitted, and app code
 * omits both.
 */
export interface UseHomeLabSubmitDeps {
  /** `language` is the second positional param, matching `launchFocusGroup`'s own signature. */
  launchFocusGroup?: (
    segment: HomeLabSegment,
    language: string | null,
    options?: FocusGroupLaunchOptions,
  ) => Promise<FocusGroupLaunchResult>
  createThread?: (input: {
    scope: KnowledgeThreadScope
    language?: string
    sourcePromptId?: string
  }) => Promise<KnowledgeThreadListItem>
}

export function useHomeLabSubmit(deps?: UseHomeLabSubmitDeps): UseHomeLabSubmitResult {
  const router = useRouter()
  const closedStudyTypes = useClosedStudyTypes()
  /* Defense in depth for the library gate's D4 lift, mirroring `HomeLabStage`'s own call to
     the same hook: `HomeComposer` already withholds `onSubmit` while `submitBlocked` is
     true, but `runSubmit`'s own guard below must agree with what the flag says regardless
     of who called it. */
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  const createStudyEnabled = useCreateStudyEnabled()

  const [state, setState] = useState<HomeLabSubmitState>(IDLE_STATE)

  /* Held in a ref, not read straight out of `deps`, so `submit` keeps ONE identity across
     renders. A caller passing a fresh object literal every render would otherwise rebuild
     the callback every render and defeat every memo the stage puts around it. The ref is
     typed by the concrete function, not by the optional `deps` field, so `.current` is never
     `undefined` from the type checker's own point of view: the `??` fallback below always
     runs before the ref is read. */
  const launchRef = useRef<
    (
      segment: HomeLabSegment,
      language: string | null,
      options?: FocusGroupLaunchOptions,
    ) => Promise<FocusGroupLaunchResult>
  >(deps?.launchFocusGroup ?? launchFocusGroup)
  launchRef.current = deps?.launchFocusGroup ?? launchFocusGroup

  /* Same seam, for the data branch's thread creation. */
  const createThreadRef = useRef<
    (input: {
      scope: KnowledgeThreadScope
      language?: string
      sourcePromptId?: string
    }) => Promise<KnowledgeThreadListItem>
  >(deps?.createThread ?? knowledgeThreadsApi.create)
  createThreadRef.current = deps?.createThread ?? knowledgeThreadsApi.create

  /* True from the moment a submit starts until it navigates or refuses. See the guard in
     `submit` for why this is a ref and not part of `state`. */
  const inFlightRef = useRef(false)

  const reset = useCallback(() => {
    inFlightRef.current = false
    setState(IDLE_STATE)
  }, [])

  const runSubmit = useCallback(
    async (input: HomeLabSubmitInput): Promise<void> => {
      // Defense in depth: `HomeComposer` already withholds `onSubmit` while
      // `submitBlocked` is true, but a caller that reaches this hook directly (a test,
      // a future surface) must not be able to run a study with no audience chosen.
      // Since Data Mode Step 5 this same call is also what stops a data-mode hand-off
      // from being issued against a library that has nothing searchable in it — unless
      // Assistant Platform v1's flag has lifted that precondition (D4, see the function's
      // own doc comment).
      if (!isHomeLabSubmitReady(input, { assistantPlatformEnabled })) {
        return
      }

      if (input.mode === 'study') {
        if (!createStudyEnabled) {
          setState({
            status: 'failed',
            message: i18n.t('blockedFeature.studyCreation', { ns: 'app' }),
          })
          return
        }
        if (isStudyTypeClosed(closedStudyTypes, input.studyTypeId)) {
          setState({
            status: 'failed',
            message: i18n.t('homeLab.typeUnavailable', { ns: 'app' }),
          })
          return
        }
        /* Audience is optional on this hand-off: the questionnaire builder can
           pick and filter it so the first draft is not blocked on a home pick.
           STEP 10: this reads `audienceId`, not `segmentKey`. */
        const handoff = buildStudyCreationHandoff({
          audienceId: input.segment?.audienceId ?? null,
          message: input.message,
          studyTypeId: input.studyTypeId,
          language: input.language,
        })
        router.push(handoff.href, { state: handoff.state })
        return
      }

      if (input.mode === 'chat') {
        /* `isHomeLabSubmitReady` already proved `segment` is non-null for this branch, and
           `launchFocusGroup` refuses on an empty audience id anyway. */
        setState({ status: 'pending', message: null })

        /* `launchFocusGroup` returns its refusals rather than throwing them, so this try
           only exists to honour this hook's contract: `submit` never rejects, so a caller
           can `void submit(...)` from an event handler. */
        let result: FocusGroupLaunchResult
        try {
          result = await launchRef.current(input.segment!, input.language, input.focusGroupOptions ?? {})
        } catch (error) {
          const blocked = blockedFeatureMessage(error)
          setState({
            status: 'failed',
            message: blocked
              ?? (error instanceof Error && error.message.length > 0
                ? i18n.t('homeLab.focusGroup.startFailedWithDetail', {
                    ns: 'app',
                    detail: error.message,
                  })
                : i18n.t('homeLab.focusGroup.startFailed', { ns: 'app' })),
          })
          return
        }

        if (!result.ok) {
          /* A capped trial is an upgrade prompt, not a broken request, so it carries no
             inline message: the stage opens `TrialLimitUpgradeModal` off the status. */
          if (result.reason === 'trial_capped') {
            setState({ status: 'trial_capped', message: null })
            return
          }
          setState({ status: 'failed', message: result.message })
          return
        }

        /* The room exists. `initialMessage` is router state and not a room setting because
           `ChatPage` auto-sends exactly that key once the intros have posted. */
        stashHomeStimuli(result.token, input.stimuli ?? [])
        router.push(ROUTES.chatRoom(result.token), {
          state: { initialMessage: input.message.trim() },
        })
        /* Settle rather than stay pending: this hook is about to unmount with the composer,
           and if the navigation ever failed to take, a composer stuck busy would be worse
           than one that briefly un-busies. */
        setState(IDLE_STATE)
        return
      }

      /* DATA. Mints a real knowledge thread and lands on it. See the header: this mirrors
         `ThreadsPage.tsx`'s own `handleSend`, not a second way of starting a thread.
         `HomeLabStage` now owns the picker; omitted `input.scope` stays Any. */
      setState({ status: 'pending', message: null })

      // HomeLabStage already normalizes the complete scope at persistableAskScope.
      // Carry it unchanged to both creation and navigation; rebuilding a subset here
      // dropped Library collections, source mode and social limits.
      const scope: KnowledgeThreadScope = input.scope ?? {
        mode: 'all',
        documentTokens: [],
        collectionTokens: [],
        web: true,
        library: true,
      }
      const trimmedMessage = input.message.trim()

      try {
        /* `language` rides along ONLY when non-null (Composer Language plan, D2/D4): a null
           effective language means nothing was resolved, so the field is omitted rather than
           sent as an empty string, and the backend's own default stays in force. Binds to the
           `POST /knowledge/threads` contract landed in the same wave. */
        const thread = await createThreadRef.current({
          scope,
          ...(input.language ? { language: input.language } : {}),
          ...(input.sourcePromptId
            ? { sourcePromptId: input.sourcePromptId }
            : {}),
        })
        upsertSidebarThread(thread)
        stashHomeStimuli(thread.token, input.stimuli ?? [])
        /* `pendingMessage`, not `initialMessage`: `ThreadDetailPage` reads exactly this key
           off location state and clears it before running the ask (`ThreadDetailPage.tsx:472`).
           `pendingEffort` rides along the same way `language` does above: omitted, not
           defaulted, when the caller did not state one, so a caller that predates the
           effort toggle (every test that does not set `input.effort`) produces the exact
           state object it always has. Step 6.3/7.1 is the consumer, once the thread page
           fires the ask that this value is for. `sourcePromptId` is stamped on the thread
           row at create (R3.6); router state keeps it visible for the hand-off. */
        router.push(ROUTES.thread(thread.token), {
          state: {
            pendingMessage: trimmedMessage,
            ...(input.effort ? { pendingEffort: input.effort } : {}),
            ...(input.profile ? { pendingProfile: input.profile } : {}),
            ...(input.sourcePromptId
              ? { sourcePromptId: input.sourcePromptId }
              : {}),
            scope,
          },
        })
        setState(IDLE_STATE)
      } catch (error) {
        /* No trial cap is known to apply to thread creation, so unlike the chat branch this
           has only one failure shape: any thrown error is `failed`. The assistant kill
           switch is a 403 whose Axios message is the status line; show contact support. */
        const blocked = blockedFeatureMessage(error)
        setState({
          status: 'failed',
          message:
              blocked
              ?? (error instanceof Error && error.message.length > 0
                ? i18n.t('homeLab.thread.startFailedWithDetail', {
                    ns: 'app',
                    detail: error.message,
                  })
                : i18n.t('homeLab.thread.startFailed', { ns: 'app' })),
        })
      }
    },
    [closedStudyTypes, router, assistantPlatformEnabled, createStudyEnabled],
  )

  /**
   * RE-ENTRANCY, guarded here rather than only in the composer.
   *
   * `HomeComposer` already blocks a second activation through `isBusy`, but that block
   * only exists one React commit after `pending` is set, and only for callers that pass
   * the prop at all. A chat submit that runs twice does not merely render twice, it
   * CREATES TWO ROOMS and bills for both, so the guard belongs next to the work rather
   * than next to the button. `HomeStage.handleStartChat` guards the same way with its own
   * `if (isCreatingRoom) return`.
   *
   * A ref, not state: this has to read true synchronously within the same tick, which is
   * exactly what a re-render cannot promise. `finally` rather than a clear at each exit
   * because `runSubmit` returns from seven places and one missed clear would wedge the
   * composer permanently.
   */
  const submit = useCallback(
    async (input: HomeLabSubmitInput): Promise<void> => {
      if (inFlightRef.current) {
        return
      }
      inFlightRef.current = true
      try {
        await runSubmit(input)
      } finally {
        inFlightRef.current = false
      }
    },
    [runSubmit],
  )

  return { submit, state, reset }
}
